import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { removeCertificate } from '@app/store/actions/certificate.actions';
import { removeSession, setSession } from '@app/store/actions/session.actions.';
import { GlobalState } from '@app/store/store';
import { Store } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';
import { LocalStorageService } from 'ngx-webstorage';
import { Observable, lastValueFrom, tap } from 'rxjs';
import { CorrelationIdService } from '../correlation-id.service';
import { UserSession } from './../../store/models/session.model';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  baseUrl: string = '/ncfr-api/session';
  correlationId: string;

  constructor(
    private httpClient: HttpClient,
    private store: Store<GlobalState>,
    private localStorageService: LocalStorageService,
    private router: Router,
    private cookieService: CookieService,
    private correlationIdService: CorrelationIdService
  ) {
    this.correlationIdService
      .getCorrelationId()
      .subscribe(value => (this.correlationId = value));
  }

  getSession(): Observable<UserSession> {
    return this.httpClient
      .get<UserSession>(this.baseUrl, {
        headers: {
          'InfoCert-Correlation-ID': this.correlationId,
        },
      })
      .pipe(
        tap(userSession => {
          this.store.dispatch(setSession(userSession));
          return userSession;
        })
      );
  }

  async getUserSession(clearSession?: any): Promise<UserSession> {
    // genero la correlationId della sessione refreshata
    this.correlationIdService.generateCorrelationId();

    // oggetto headers
    const headers: Record<string, string> = {
      'InfoCert-Correlation-ID': this.correlationId,
    };

    if (clearSession !== null && clearSession !== undefined) {
      // aggiungo 'Clear-Session' se clearSession ha valore
      headers['Clear-Session'] = clearSession;
      this.localStorageService.clear('clearSession');
    }

    try {
      const userSession = await lastValueFrom(
        this.httpClient.get<UserSession>(this.baseUrl, {
          headers,
        })
      );

      // Salvo la sessione sia nello store che nel localStorage
      this.store.dispatch(setSession(userSession));
      this.localStorageService.store('session', userSession);

      return userSession;
    } catch (error) {
      // Se c'è un errore, mantengo la sessione esistente se presente
      const existingSession = this.localStorageService.retrieve('session');
      if (existingSession) {
        return existingSession;
      } else {
        this.logout();
      }
      throw error;
    }
  }

  login(username: string, password: string): Observable<UserSession> {
    console.log('🚀 ~ SessionService ~ login ', { password, username });

    let params = new HttpParams({
      fromObject: { password, username },
    });

    // genero la correlationId della sessione loggata
    this.correlationIdService.generateCorrelationId();

    return this.httpClient
      .post<UserSession>(
        this.baseUrl,
        { password, username },
        {
          headers: {
            'X-REQUESTED-FOR': 'authentication',
            ignoreLoading: 'true',
            'InfoCert-Correlation-ID': this.correlationId,
          },
        }
      )
      .pipe(
        tap(userSession => {
          this.store.dispatch(setSession(userSession));
          return userSession;
        })
      );
  }

  async logout(): Promise<void> {
    try {
      await lastValueFrom(
        this.httpClient.delete<void>(this.baseUrl, {
          // ignore logout session error
          headers: {
            ignoreInterceptor: 'true',
            'InfoCert-Correlation-ID': this.correlationId,
          },
        })
      );
    } catch (error) {
    } finally {
      // mi salvo puk e needActivation come variabili
      const puk = this.localStorageService.retrieve('puk');
      const needActivation =
        this.localStorageService.retrieve('needActivation');

      this.store.dispatch(removeSession());
      this.store.dispatch(removeCertificate());

      // svuoto la local storage e i cookie, preservando solo identity
      const identity = localStorage.getItem('identity');
      this.localStorageService.clear();
      this.cookieService.deleteAll();

      // pulisco anche la sessionStorage (include i token OIDC salvati lì),
      // poi re-imposto la flag anti auto-login post-logout.
      sessionStorage.clear();

      if (identity) {
        console.log('🚀 ~  identity found:', identity);
        localStorage.setItem('identity', identity);
      }

      // svuoto la correlationId della session
      this.correlationIdService.resetCorrelationId();

      // blocca auto-login dopo logout su semplice Ctrl+R
      sessionStorage.setItem('blockAutoLoginAfterLogout', 'true');

      // se la variabile needActivation esiste allora mi setto nella local storage il puk e needActivation (serve per il passo 2 di prima attivazione in caso di refresh)
      if (needActivation) {
        this.localStorageService.store('puk', puk);
        this.localStorageService.store('needActivation', needActivation);
      }

      this.router.navigate(['/login']); /* .then(() => {
        // reload forzato per ricaricare il banner pubblicitario dopo una logout
        window.location.reload();
      }); */
    }
  }

  logoutNoSession() {
    // mi salvo puk e needActivation come variabili
    const puk = this.localStorageService.retrieve('puk');
    const needActivation = this.localStorageService.retrieve('needActivation');

    this.store.dispatch(removeSession());
    this.store.dispatch(removeCertificate());

    //mi salvo l'identity se esiste
    const identity = localStorage.getItem('identity');
    // svuoto la local storage e i cookie
    this.localStorageService.clear();
    this.cookieService.deleteAll();

    // pulisco anche la sessionStorage (include i token OIDC salvati lì),
    // poi re-imposto la flag anti auto-login post-logout.
    sessionStorage.clear();

    // se l'identity esiste lo ri-salvo nella local storage che è stata appena svuotata
    if (identity) {
      localStorage.setItem('identity', identity);
    }
    // svuoto la correlationId della session
    this.correlationIdService.resetCorrelationId();

    // blocca auto-login dopo logout su semplice Ctrl+R
    sessionStorage.setItem('blockAutoLoginAfterLogout', 'true');

    // se la variabile needActivation esiste allora mi setto nella local storage il puk e needActivation (serve per il passo 2 di prima attivazione in caso di refresh)
    if (needActivation) {
      this.localStorageService.store('puk', puk);
      this.localStorageService.store('needActivation', needActivation);
    }

    if (window.location.toString().includes('/oauth2')) {
      this.router.navigate(['/oauth2']);
    } else if (window.location.toString().includes('/error')) {
      this.router.navigate(['/error']);
    } else {
      this.router.navigate(['/login']);
    }
  }

  forcePWD(puk: string, newPwd: string): Observable<any> {
    return this.httpClient.put<any>(
      this.baseUrl + '/force-access',
      { puk, newPwd },
      {
        headers: {
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }

  updatePWD(oldPwd: string, newPwd: string): Observable<any> {
    return this.httpClient.put<any>(
      this.baseUrl + '/access-info',
      {
        newPwd,
        oldPwd,
      },
      {
        headers: {
          'InfoCert-Correlation-ID': this.correlationId,
        },
      }
    );
  }
}
